import { useEffect, useRef, useCallback } from 'react';
import { useGlobalProgressBarStore, ProgressState } from '@/store/ui/progress-bar.store';
import { getEstadoIngresoInventario } from '@/shared/actions/erp/inventario/ingreso-producto.actions';

interface UseProgressPollingOptions {
  enabled: boolean;
  interval?: number;
}

export const useProgressPolling = ({
  enabled,
  interval = 2000
}: UseProgressPollingOptions) => {
  const currentTask = useGlobalProgressBarStore(s => s.currentTask);
  const updateProgress = useGlobalProgressBarStore(s => s.updateProgress);
  const clearTask = useGlobalProgressBarStore(s => s.clearTask);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const poll = useCallback(async () => {
    if (!currentTask) return;

    try {
      const response = await getEstadoIngresoInventario(currentTask.codigo);

      if (response) {
        const progressState: ProgressState = {
          estado: response.estado || 'PROCESANDO',
          progreso: response.progreso || 0,
          total: response.total || 0,
          porcentaje: response.porcentaje || 0,
          descripcion: response.descripcion || '',
          error: response.error,
        };

        updateProgress(progressState);

        // Si terminó (completado o error), detener el polling
        if (progressState.estado === 'COMPLETADO' || progressState.estado === 'ERROR') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          // Auto-cerrar después de 5 segundos si fue exitoso
          if (progressState.estado === 'COMPLETADO') {
            setTimeout(() => {
              clearTask();
            }, 5000);
          }
        }
      }
    } catch (error: unknown) {
      console.error('Error polling progress:', error);

      // Si el error es 404, significa que el registro fue eliminado debido a un error en el procesamiento
      // (ej: series duplicadas). Mostrar estado de error y detener el polling.
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 404) {
        const errorState: ProgressState = {
          estado: 'ERROR',
          progreso: 0,
          total: 0,
          porcentaje: 0,
          descripcion: 'El proceso falló. Por favor verifique los datos e intente nuevamente.',
          error: 'El ingreso fue eliminado debido a un error en el procesamiento (posibles series duplicadas u otro error de validación).',
        };

        updateProgress(errorState);

        // Detener el polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }
  }, [currentTask, updateProgress, clearTask]);

  useEffect(() => {
    if (enabled && currentTask) {
      // Ejecutar inmediatamente
      poll();

      // Configurar intervalo
      intervalRef.current = setInterval(poll, interval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, currentTask, poll, interval]);

  return { poll };
};
